/**
 * ¿HAY UNA SESIÓN GUARDADA EN ESTE DISPOSITIVO? Preguntado SÍN ESPERAR A NADIE.
 *
 * EL FALLO QUE ARREGLA (29-08-2026). Al abrir la app, durante un instante se veía
 * la pantalla de entrar —correo y contraseña— y justo después el «Hola de nuevo».
 * En los tres dispositivos, y solo un parpadeo.
 *
 * No es la sesión: la sesión está. Es que `AuthContext` arranca con
 * `loading = true` y lo suelta en cuanto `getSession()` contesta... y
 * `getSession()` puede contestar NULL teniendo la sesión guardada en el disco:
 * si el token de acceso ha caducado —cualquier arranque más de una hora después
 * del anterior— la biblioteca tiene que renovarlo contra el servidor, y si esa
 * primera petición falla o llega tarde (un teléfono que acaba de despertar, un
 * invernadero con mala cobertura) devuelve «no hay sesión» mientras el fichero
 * sigue ahí. Con `loading` en falso y sin usuario, `/app-auth` pinta el
 * formulario. Luego la renovación entra, aparece el usuario, y la app salta al
 * panel. Eso es exactamente el parpadeo.
 *
 * El disco no miente y responde EN EL ACTO. Con esto, `AuthContext` puede
 * distinguir las dos cosas que hoy se confunden en un mismo «no hay usuario»:
 *
 *   · no hay sesión  → a la pantalla de entrar, sin esperar a nadie.
 *   · hay sesión y todavía no ha llegado → esperar, que viene.
 *
 * `RoleBasedRouter` ya hacía una versión de esto por su cuenta (`sessionTransition`,
 * preguntando otra vez a `getSession`), lo que dice que la carrera se conocía; lo
 * que faltaba era saberlo ANTES de pintar nada, y en un solo sitio.
 */

/**
 * La llave la fabrica supabase-js como `sb-<ref>-auth-token`, con el `ref` del
 * subdominio del proyecto. No se calcula aquí a mano a propósito: si un día
 * cambia el proyecto o la biblioteca parte la sesión en trozos numerados
 * (`...-auth-token.0`), esto sigue valiendo.
 */
const CLAVE_DE_SESION = /^sb-.+-auth-token(\.\d+)?$/;

export function hasStoredSupabaseSession(): boolean {
  try {
    if (typeof localStorage === "undefined") return false;
    for (let i = 0; i < localStorage.length; i++) {
      const clave = localStorage.key(i);
      if (!clave || !CLAVE_DE_SESION.test(clave)) continue;
      const valor = localStorage.getItem(clave);
      /* La cadena vacía es la lápida de cierre de sesión que deja el
         almacenamiento de la vista previa de Lovable: existe la llave y NO hay
         sesión. Cualquier otra cosa con contenido es una sesión guardada. */
      if (valor && valor.length > 20) return true;
    }
  } catch {
    /* Modo privado, almacenamiento bloqueado por el navegador, cuota llena:
       sin poder mirar, se contesta que no y todo sigue como antes. */
  }
  return false;
}

/**
 * Cuánto se espera, como mucho, a que llegue esa sesión antes de dar la app por
 * cerrada y enseñar la pantalla de entrar.
 *
 * Por debajo de los 3 s de `ColdStartGate`, que es quien tapa la app mientras
 * tanto: así el orden es siempre el mismo —se espera, se rinde, y ENTONCES se
 * levanta el telón—, y nunca al revés. Si la renovación acaba llegando más
 * tarde, el usuario ya estará en la pantalla de entrar y entrará solo, que es
 * justo lo que pasaba antes a los 200 ms.
 */
export const ESPERA_MAX_SESION_MS = 2500;

/**
 * Y cuánto se espera DONDE NO HAY TELÓN.
 *
 * `ColdStartGate` exime `/app-auth` —allí se hace el apretón de manos de la
 * identificación— y resulta que ésa es justo la ruta en la que el envoltorio
 * nativo abre la app (lo enseñó la caja negra del arranque:
 * `/app-auth?onesignal_push_id=…`). Sin telón detrás, los 2,5 s de arriba se
 * quedan cortos: cuando la renovación del token tarda más —token caducado y
 * cobertura de invernadero— se ve medio segundo de «Crear cuenta gratis».
 *
 * Aquí se puede esperar más que en el resto de la aplicación porque lo que se
 * enseña mientras tanto no es una pantalla a medias: es nada, y detrás está el
 * arranque del propio contenedor. El tope existe solo para que alguien
 * REALMENTE sin cobertura pueda acabar entrando a mano.
 */
export const ESPERA_MAX_SESION_SIN_TELON_MS = 8000;

/**
 * Y CUÁNTO SE ESPERA A SABER A DÓNDE LLEVAR A ALGUIEN QUE YA ESTÁ DENTRO.
 *
 * EL FALLO (06-09-2026, la app no abría en el teléfono). `AppAuth` se calla
 * mientras hay usuario y todavía no se sabe el destino. Ese silencio no tenía
 * tope: la única salida era que la consulta del destino contestara. Si ese
 * `setState` no llegaba nunca —la consulta se queda colgada, o el efecto se
 * cancela y se vuelve a lanzar más rápido de lo que tarda en contestar— la
 * pantalla se quedaba EN BLANCO para siempre, sin error, sin nada que tocar y
 * sin manera de entrar a mano.
 *
 * Es el camino que solo pisa el teléfono: el envoltorio nativo abre la app en
 * `/app-auth`, mientras que en el navegador se entra por `/dashboard` y esta
 * pantalla no se monta. Por eso en el ordenador no se veía.
 *
 * La regla que se saca de aquí: una pantalla puede callarse un rato, pero nunca
 * puede callarse SIN RELOJ. Si al cabo de esto no hay destino, se enseña la
 * portada: es fea de ver estando ya dentro, pero se puede usar, y eso siempre
 * gana. Diez segundos: más que los 8 de arriba, porque esto ocurre DESPUÉS de
 * que la sesión haya llegado, y menos que el fallo de la pantalla de arranque.
 */
export const ESPERA_MAX_DESTINO_MS = 10000;

/**
 * Y CUÁNTO SE ESPERA AL PERFIL, cuando ya hay sesión.
 *
 * EL FALLO (08-09-2026). `AuthContext.fetchUserData` hace `setLoading(true)` y se
 * pone a esperar el perfil sin tope. Su `finally` cubre que la consulta conteste
 * o falle; lo que no cubre es que no vuelva NUNCA, que es lo que hace un `fetch`
 * en una red que se cae a medias. Y con `loading` en `true` para siempre, el
 * primer guardián de `AppAuth` deja la app en blanco sin remedio.
 *
 * Es hermano del de `ESPERA_MAX_DESTINO_MS`: misma regla —ninguna espera sin
 * reloj— en el otro sitio donde faltaba.
 *
 * 12 s, más que los demás, a propósito: al vencer se sigue sin perfil y eso
 * puede acabar en la pantalla de entrar. Vale la pena solo si la consulta está
 * de verdad colgada, no si va lenta.
 */
export const ESPERA_MAX_PERFIL_MS = 12000;

/**
 * Y EL SUELO DE TODO: CUÁNTO SE ESPERA A QUE LA BIBLIOTECA CONTESTE SIQUIERA.
 *
 * EL FALLO (08-09-2026, la tercera). Los dos relojes de arriba —y el del
 * perfil— tienen algo en común que no se ve hasta que lo buscas: TODOS se
 * arman DENTRO del `.then` de `supabase.auth.getSession()`. O sea que ninguno
 * existe hasta que esa promesa contesta. Y esa promesa no tenía ni `.catch()`
 * ni tope.
 *
 * `loading` nace en `true`. Si `getSession()` no contesta NUNCA —el `fetch` de
 * la renovación del token colgado en una red que se está cayendo, que ni
 * resuelve ni rechaza; ver `consultas-que-fallan-en-silencio`— nadie lo suelta,
 * y `AppAuth` deja la pantalla en blanco para siempre.
 *
 * POR QUÉ ESTO EXPLICA LO DE «A VECES SÍ Y A VECES NO». Las otras dos causas
 * dependían del código: con el código roto fallaban siempre. Ésta depende de la
 * RED en el segundo exacto en que se abre la app. Por eso el mismo teléfono, con
 * la misma versión, unas veces entra y otras se queda mirando el blanco.
 *
 * 15 s, y es a propósito el más largo de todos: esto NO es un tope para ir
 * rápido, es un suelo para que exista una salida. Tiene que quedar por encima de
 * cualquier arranque lento de verdad (renovar el token con mala cobertura) para
 * no resucitar el parpadeo de la pantalla de entrar, y por debajo de los 20 s
 * del vigía del `index.html`, para que la app se rinda ANTES de que el HTML
 * decida recargarla.
 */
export const ESPERA_MAX_ARRANQUE_MS = 15000;
